'use client'

import { useState, useCallback } from 'react'
import { GITHUB_CLIENT_ID } from '@/lib/config'
import { loginAttempt, loginFailure, peekLandingLoginEntry } from '@/lib/ga'

interface UseGithubAuthReturn {
  handleGithubLogin: () => Promise<void>
  isLoading: boolean
  error: string | null
}

export function useGithubAuth(): UseGithubAuthReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleGithubLogin = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!GITHUB_CLIENT_ID) {
        const errorMsg = 'GitHub Client ID is not configured'
        setError(errorMsg)
        reject(new Error(errorMsg))
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        // Generate a random state for CSRF protection
        const state = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
        
        // Store state in localStorage for validation in callback (shared across tabs of same origin)
        if (typeof window !== 'undefined') {
          localStorage.setItem('github_oauth_state', state)
        }

        // Construct redirect URI dynamically based on current origin
        const redirectUri = `${window.location.origin}/auth/github/callback`

        // GitHub OAuth URL
        const githubAuthUrl = `https://github.com/login/oauth/authorize?client_id=${GITHUB_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=user:email&state=${state}`

        // Redirect to GitHub once the analytics hit has left the page
        void loginAttempt({
          method: 'github',
          login_entry: peekLandingLoginEntry(),
        }).then(() => {
          window.location.href = githubAuthUrl
        })
        
        // Note: We don't resolve here because the page will redirect
        // The callback route will handle the rest
      } catch (err) {
        setIsLoading(false)
        const errorMsg = err instanceof Error ? err.message : 'An error occurred during GitHub login'
        setError(errorMsg)
        loginFailure({
          method: 'github',
          login_entry: peekLandingLoginEntry(),
          failure_step: 'github',
          error_message: errorMsg,
        })
        reject(new Error(errorMsg))
      }
    })
  }, [])

  return {
    handleGithubLogin,
    isLoading,
    error,
  }
}

